import React, { useState, Children } from 'react';
import { IconArrowRight, IconArrowLeft, IconPlus, IconTrash, IconUser, IconHome, IconUserPlus, IconLink } from '@tabler/icons-react';
interface OnboardingPageProps {
  onComplete: () => void;
}
type OnboardingStep = 'welcome' | 'family-choice' | 'create-family' | 'join-family' | 'children';
type ParentType = 'mamma' | 'papa' | 'altro';
interface Child {
  id: string;
  name: string;
  birthDate: string;
}
export function OnboardingPage({
  onComplete
}: OnboardingPageProps) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [parentType, setParentType] = useState<ParentType | null>(null);
  const [familyName, setFamilyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [children, setChildren] = useState<Child[]>([]);
  const [newChildName, setNewChildName] = useState('');
  const [newChildBirthDate, setNewChildBirthDate] = useState('');
  const calculateAge = (birthDateString: string) => {
    if (!birthDateString) return '';
    const today = new Date();
    const birth = new Date(birthDateString);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birth.getDate()) {
      age--;
    }
    return age > 0 ? `${age} anni` : '< 1 anno';
  };
  const handleAddChild = () => {
    if (newChildName && newChildBirthDate) {
      setChildren([...children, {
        id: Date.now().toString(),
        name: newChildName,
        birthDate: newChildBirthDate
      }]);
      setNewChildName('');
      setNewChildBirthDate('');
    }
  };
  const handleComplete = () => {
    console.log('Onboarding complete:', {
      parentType,
      familyName,
      children
    });
    onComplete();
  };
  const handleJoinFamily = () => {
    if (joinCode && parentType) {
      console.log('Joining family:', joinCode);
      onComplete();
    }
  };
  // Step 1: Welcome - Full screen immersive
  if (currentStep === 'welcome') {
    return <div className="min-h-screen flex flex-col bg-yeppy-bg" data-id="element-896">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12" data-id="element-897">
          {/* Logo */}
          <div className="mb-12" data-id="element-898">
            <img src="https://cdn.magicpatterns.com/uploads/bgucnc6iAv4SE4noNyVpoM/Logo_esteso.svg" alt="Yeppy" className="h-10 mx-auto" data-id="element-899" />
          </div>

          {/* Welcome content */}
          <div className="text-center mb-16 max-w-sm" data-id="element-900">
            <h1 className="text-3xl text-yeppy-text mb-4" data-id="element-901">Benvenuto! 👋</h1>
            <p className="text-base text-yeppy-text text-opacity-60 leading-relaxed" data-id="element-902">
              Yeppy ti aiuta a gestire il{' '}
              <span className="bg-yeppy-yellow bg-opacity-40 px-2 py-0.5 rounded-md" data-id="element-903">
                carico mentale
              </span>{' '}
              della vita da genitore
            </p>
          </div>

          {/* Large CTA Button */}
          <button onClick={() => setCurrentStep('family-choice')} className="w-full max-w-xs flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]" style={{
          backgroundColor: '#1D2A36',
          color: '#FFFFFB'
        }} data-id="element-904">
            Inizia ora
            <IconArrowRight className="w-5 h-5" stroke={1.5} data-id="element-905" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-8" data-id="element-906">
          <div className="w-8 h-2 rounded-full bg-yeppy-text" data-id="element-907" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-908" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-909" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-910" />
        </div>
      </div>;
  }
  // Step 2: Family Choice - Immersive cards
  if (currentStep === 'family-choice') {
    return <div className="min-h-screen flex flex-col bg-yeppy-bg" data-id="element-911">
        {/* Header */}
        <div className="px-6 pt-8 pb-4" data-id="element-912">
          <button onClick={() => setCurrentStep('welcome')} className="w-12 h-12 rounded-full bg-white border border-yeppy-text border-opacity-10 flex items-center justify-center mb-6" data-id="element-913">
            <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={1.5} data-id="element-914" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12" data-id="element-915">
          <div className="text-center mb-10 max-w-sm" data-id="element-916">
            <h1 className="text-2xl text-yeppy-text mb-3" data-id="element-917">
              Come vuoi iniziare?
            </h1>
            <p className="text-sm text-yeppy-text text-opacity-60" data-id="element-918">
              Scegli l'opzione che fa per te
            </p>
          </div>

          {/* Large Option Cards */}
          <div className="w-full max-w-sm space-y-4" data-id="element-919">
            <button onClick={() => setCurrentStep('create-family')} className="w-full flex flex-col items-center gap-4 p-8 rounded-3xl bg-white border-2 border-yeppy-text border-opacity-10 transition-all text-center group" data-id="element-920">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors" style={{
              backgroundColor: '#1D2A36'
            }} data-id="element-921">
                <IconHome className="w-8 h-8 text-white" stroke={1.5} data-id="element-922" />
              </div>
              <div data-id="element-923">
                <p className="text-lg text-yeppy-text mb-1" data-id="element-924">
                  Crea un nuovo nucleo
                </p>
                <p className="text-sm text-yeppy-text text-opacity-50" data-id="element-925">
                  Aggiungi figli e invita caregiver
                </p>
              </div>
            </button>

            <button onClick={() => setCurrentStep('join-family')} className="w-full flex flex-col items-center gap-4 p-8 rounded-3xl bg-white border-2 border-yeppy-text border-opacity-10 transition-all text-center group" data-id="element-926">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-colors" style={{
              backgroundColor: '#1D2A36'
            }} data-id="element-927">
                <IconUserPlus className="w-8 h-8 text-white" stroke={1.5} data-id="element-928" />
              </div>
              <div data-id="element-929">
                <p className="text-lg text-yeppy-text mb-1" data-id="element-930">
                  Unisciti a un nucleo
                </p>
                <p className="text-sm text-yeppy-text text-opacity-50" data-id="element-931">
                  Hai ricevuto un invito?
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-8" data-id="element-932">
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-933" />
          <div className="w-8 h-2 rounded-full bg-yeppy-text" data-id="element-934" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-935" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-936" />
        </div>
      </div>;
  }
  // Step 3a: Join Family
  if (currentStep === 'join-family') {
    return <div className="min-h-screen flex flex-col bg-yeppy-bg" data-id="element-937">
        {/* Header */}
        <div className="px-6 pt-8 pb-4" data-id="element-938">
          <button onClick={() => setCurrentStep('family-choice')} className="w-12 h-12 rounded-full bg-white border border-yeppy-text border-opacity-10 flex items-center justify-center mb-6" data-id="element-939">
            <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={1.5} data-id="element-940" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col px-6 pb-12" data-id="element-941">
          <div className="text-center mb-10" data-id="element-942">
            <h1 className="text-2xl text-yeppy-text mb-3" data-id="element-943">
              Unisciti al nucleo
            </h1>
            <p className="text-sm text-yeppy-text text-opacity-60" data-id="element-944">
              Inserisci il codice che hai ricevuto
            </p>
          </div>

          {/* Form */}
          <div className="w-full max-w-sm mx-auto space-y-6 flex-1" data-id="element-945">
            <div data-id="element-946">
              <label className="block text-sm text-yeppy-text text-opacity-70 mb-3" data-id="element-947">
                Codice invito
              </label>
              <div className="relative" data-id="element-948">
                <IconLink className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-yeppy-text text-opacity-40" stroke={1.5} data-id="element-949" />
                <input type="text" value={joinCode} onChange={e => setJoinCode(e.target.value)} placeholder="Es. ABC123" className="w-full pl-14 pr-5 py-4 rounded-2xl text-base text-yeppy-text bg-white border-2 border-yeppy-text border-opacity-10 focus:outline-none focus:border-yeppy-blue transition-colors placeholder-yeppy-text placeholder-opacity-40" data-id="element-950" />
              </div>
            </div>

            <div data-id="element-951">
              <label className="block text-sm text-yeppy-text text-opacity-70 mb-3" data-id="element-952">
                Il tuo ruolo
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-953">
                {['Mamma', 'Papà', 'Altro'].map(role => <button key={role} onClick={() => setParentType(role.toLowerCase() as ParentType)} className={`py-4 rounded-2xl text-base transition-all ${parentType === role.toLowerCase() ? 'bg-yeppy-blue text-yeppy-text border-2 border-yeppy-blue' : 'bg-white border-2 border-yeppy-text border-opacity-10 text-yeppy-text'}`} data-id="element-954">
                    {role}
                  </button>)}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="w-full max-w-sm mx-auto mt-8" data-id="element-955">
            <button onClick={handleJoinFamily} disabled={!joinCode || !parentType} className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-50" style={{
            backgroundColor: '#1D2A36',
            color: '#FFFFFB'
          }} data-id="element-956">
              Unisciti
              <IconArrowRight className="w-5 h-5" stroke={1.5} data-id="element-957" />
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-8" data-id="element-958">
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-959" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-960" />
          <div className="w-8 h-2 rounded-full bg-yeppy-text" data-id="element-961" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-962" />
        </div>
      </div>;
  }
  // Step 3b: Create Family
  if (currentStep === 'create-family') {
    return <div className="min-h-screen flex flex-col bg-yeppy-bg" data-id="element-963">
        {/* Header */}
        <div className="px-6 pt-8 pb-4" data-id="element-964">
          <button onClick={() => setCurrentStep('family-choice')} className="w-12 h-12 rounded-full bg-white border border-yeppy-text border-opacity-10 flex items-center justify-center mb-6" data-id="element-965">
            <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={1.5} data-id="element-966" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col px-6 pb-12" data-id="element-967">
          <div className="text-center mb-10" data-id="element-968">
            <h1 className="text-2xl text-yeppy-text mb-3" data-id="element-969">
              Crea il tuo nucleo
            </h1>
            <p className="text-sm text-yeppy-text text-opacity-60" data-id="element-970">
              Dai un nome alla tua famiglia
            </p>
          </div>

          {/* Form */}
          <div className="w-full max-w-sm mx-auto space-y-6 flex-1" data-id="element-971">
            <div data-id="element-972">
              <label className="block text-sm text-yeppy-text text-opacity-70 mb-3" data-id="element-973">
                Nome del nucleo
              </label>
              <input type="text" value={familyName} onChange={e => setFamilyName(e.target.value)} placeholder="Es. Famiglia Rossi" className="w-full px-5 py-4 rounded-2xl text-base text-yeppy-text bg-white border-2 border-yeppy-text border-opacity-10 focus:outline-none focus:border-yeppy-green transition-colors placeholder-yeppy-text placeholder-opacity-40" data-id="element-974" />
            </div>

            <div data-id="element-975">
              <label className="block text-sm text-yeppy-text text-opacity-70 mb-3" data-id="element-976">
                Il tuo ruolo
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-977">
                {['Mamma', 'Papà', 'Altro'].map(role => <button key={role} onClick={() => setParentType(role.toLowerCase() as ParentType)} className={`py-4 rounded-2xl text-base transition-all ${parentType === role.toLowerCase() ? 'bg-yeppy-green text-yeppy-text border-2 border-yeppy-green' : 'bg-white border-2 border-yeppy-text border-opacity-10 text-yeppy-text'}`} data-id="element-978">
                    {role}
                  </button>)}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="w-full max-w-sm mx-auto mt-8" data-id="element-979">
            <button onClick={() => setCurrentStep('children')} disabled={!familyName || !parentType} className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-50" style={{
            backgroundColor: '#1D2A36',
            color: '#FFFFFB'
          }} data-id="element-980">
              Continua
              <IconArrowRight className="w-5 h-5" stroke={1.5} data-id="element-981" />
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-8" data-id="element-982">
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-983" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-984" />
          <div className="w-8 h-2 rounded-full bg-yeppy-text" data-id="element-985" />
          <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-986" />
        </div>
      </div>;
  }
  // Step 4: Children
  return <div className="min-h-screen flex flex-col bg-yeppy-bg" data-id="element-987">
      {/* Header */}
      <div className="px-6 pt-8 pb-4" data-id="element-988">
        <button onClick={() => setCurrentStep('create-family')} className="w-12 h-12 rounded-full bg-white border border-yeppy-text border-opacity-10 flex items-center justify-center mb-6" data-id="element-989">
          <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={1.5} data-id="element-990" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pb-12" data-id="element-991">
        <div className="text-center mb-8" data-id="element-992">
          <h1 className="text-2xl text-yeppy-text mb-3" data-id="element-993">
            Aggiungi i tuoi figli
          </h1>
          <p className="text-sm text-yeppy-text text-opacity-60" data-id="element-994">
            Almeno uno per continuare
          </p>
        </div>

        <div className="w-full max-w-sm mx-auto flex-1 flex flex-col" data-id="element-995">
          {/* Add Child Form */}
          <div className="bg-white border-2 border-yeppy-text border-opacity-10 rounded-3xl p-5 mb-5" data-id="element-996">
            <div className="space-y-3 mb-4" data-id="element-997">
              <input type="text" value={newChildName} onChange={e => setNewChildName(e.target.value)} placeholder="Nome del bambino" className="w-full px-4 py-3.5 rounded-2xl text-base text-yeppy-text bg-yeppy-bg border border-yeppy-text border-opacity-10 focus:outline-none focus:border-yeppy-yellow placeholder-yeppy-text placeholder-opacity-40" data-id="element-998" />
              <input type="date" value={newChildBirthDate} onChange={e => setNewChildBirthDate(e.target.value)} max={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3.5 rounded-2xl text-base text-yeppy-text bg-yeppy-bg border border-yeppy-text border-opacity-10 focus:outline-none focus:border-yeppy-yellow" data-id="element-999" />
            </div>
            <button onClick={handleAddChild} className="w-full py-4 rounded-2xl border-2 border-dashed border-yeppy-text border-opacity-20 flex items-center justify-center gap-2 text-yeppy-text text-opacity-60 hover:border-opacity-40 hover:text-opacity-80 transition-colors" data-id="element-1000">
              <IconPlus className="w-5 h-5" stroke={1.5} data-id="element-1001" />
              <span className="font-poppins font-medium" data-id="element-1002">Aggiungi figlio</span>
            </button>
          </div>

          {/* Children List */}
          {children.length > 0 && <div className="space-y-3 mb-6 flex-1 overflow-y-auto" data-id="element-1003">
              {children.map(child => <div key={child.id} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-yeppy-text border-opacity-10" data-id="element-1004">
                  <div className="w-12 h-12 rounded-full bg-yeppy-yellow bg-opacity-20 flex items-center justify-center" data-id="element-1005">
                    <IconUser className="w-6 h-6 text-yeppy-text text-opacity-60" stroke={1.5} data-id="element-1006" />
                  </div>
                  <div className="flex-1" data-id="element-1007">
                    <p className="text-base text-yeppy-text" data-id="element-1008">{child.name}</p>
                    <p className="text-sm text-yeppy-text text-opacity-50" data-id="element-1009">
                      {calculateAge(child.birthDate)}
                    </p>
                  </div>
                  <button onClick={() => setChildren(children.filter(c => c.id !== child.id))} className="w-10 h-10 flex items-center justify-center rounded-full text-yeppy-text text-opacity-40 hover:bg-yeppy-bg hover:text-opacity-100 transition-colors" data-id="element-1010">
                    <IconTrash className="w-5 h-5" stroke={1.5} data-id="element-1011" />
                  </button>
                </div>)}
            </div>}

          {/* Complete Button */}
          <div className="mt-auto space-y-4" data-id="element-1012">
            <button onClick={handleComplete} disabled={children.length === 0} className="w-full flex items-center justify-center gap-3 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-50" style={{
            backgroundColor: '#1D2A36',
            color: '#FFFFFB'
          }} data-id="element-1013">
              Completa
              <IconArrowRight className="w-5 h-5" stroke={1.5} data-id="element-1014" />
            </button>
            <p className="text-center text-sm text-yeppy-text text-opacity-50" data-id="element-1015">
              Potrai invitare caregiver in seguito
            </p>
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2 pb-8" data-id="element-1016">
        <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-1017" />
        <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-1018" />
        <div className="w-2 h-2 rounded-full bg-yeppy-text bg-opacity-20" data-id="element-1019" />
        <div className="w-8 h-2 rounded-full bg-yeppy-text" data-id="element-1020" />
      </div>
    </div>;
}